<template>
  <div class="drip-form-container review-step-container">
    <div class="drip-form">
      <!-- header -->
      <h1 class="heading mb-0">
        {{ $t('invoices.add.titles.review') }}
      </h1>
      <p class="desc mb-0">
        {{ $t('invoices.add.subTitle.review') }}
      </p>

      <!-- sections -->
      <section
        v-for="(section, i) in sections"
        :key="i"
        class="review-section mt-5"
      >
        <div class="header d-flex justify-content-between mb-3">
          <p class="label mb-0">{{ section.title }}</p>
          <div class="edit" @click="section.onEdit">
            <b-icon-pencil-fill class="mr-1" />
            <span>{{ $t('dashboard.pendingInvoicesWidget.editText') }}</span>
          </div>
        </div>
        <component
          v-if="invoiceInformation.reference"
          :is="section.component"
          :data="section.data"
          :stackUI="section.stackUI"
          hide-empty-fields
        />
        <loader v-else :height="200" />

        <template v-if="section.subSectionLabel">
          <div class="header d-flex justify-content-between mb-3">
            <p class="label-sm mb-0">{{ section.subSectionLabel }}</p>
          </div>
          <component
            :is="section.component"
            :data="section.subSectionData"
            :stackUI="section.subSectionStackUI"
            hide-empty-fields
          />
        </template>
        <hr :key="section.title" v-if="i < sections.length - 1" />
      </section>
      <remittance-currency
        v-if="isIndia && isUSD"
        :invoice_id="parseInt($route.params.id)"
        :invoiceInformation="invoiceInformation"
        :onStatusToggleSuccess="loadInvoice"
        :isFreshUser="$store.state.root.showBannerCount === 0"
        :isInrTransactionCompleted="$store.state.root.isInrTransactionCompleted"
      />
    </div>
    <!-- footer -->
    <div class="drip-form-actions">
      <b-row>
        <b-col>
          <b-link class="mr-3 btn btn-light-purple" @click="navigateBack">
            {{ $t('invoices.add.actions.back') }}
          </b-link>
        </b-col>
      </b-row>
      <br />
      <b-row>
        <b-col>
          <form-button
            variant="secondary"
            @click="$router.push({ name: 'invoicesAdd' })"
            v-track-click
            class="d-none d-md-inline-block mr-2"
            id="invoices-upload-save-new"
          >
            {{ $t('invoices.upload.addMoreInvoices') }}
          </form-button>
          <form-button
            type="submit"
            :disabled="disableSubmit"
            variant="primary"
            @click="done(false)"
            v-track-click
            id="invoices-upload-proceed"
          >
            <span class="d-md-inline-block d-none">
              {{ $t('invoices.esign.nps.btnSubmit') }}
            </span>
            <span class="d-block d-md-none">
              {{ $t('invoices.upload.esign') }}
            </span>
          </form-button>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import FormButton from '@/components/common/FormButton'
import invoiceService from '@/services/invoice'
import accountsService from '@/services/accounts'
import ReadOnlyFields from '@/components/invoices/Add/Forms/review/ReadOnlyFields'
import ReadOnlyDocs from '@/components/invoices/Add/Forms/review/ReadOnlyDocs'
import RemittanceCurrency from '@/components/invoices/Add/components/RemittanceCurrency'
import { addInvoiceMixin } from '@/mixins/Invoice'
import InputMasking from '@/helpers/input-masking'
import moment from 'moment'
import Loader from '@/components/common/Loader'

export default {
  props: ['exporterData'],
  mixins: [addInvoiceMixin],
  components: {
    ReadOnlyFields,
    ReadOnlyDocs,
    FormButton,
    RemittanceCurrency,
    Loader
  },
  data() {
    return {
      basicDocsUploaded: false,
      prevStep: 'upload-docs',
      importer: '',
      invoiceInformation: {
        seller: '',
        reference: '',
        invoiceDate: '',
        currency: '',
        currencySymbol: '',
        grossValue: '',
        advanceReceived: '',
        value: '',
        maxAmountFormatted: '',
        maxAmount: 0,
        isDripSwitchEnabled: false
      },
      shippingDetails: {
        shippingBillDate: '',
        shippingLine: '',
        consignee: '',
        vesselName: '',
        userDueDate: '',
        containerNo: '',
        blNo: '',
        blDate: ''
      },
      fundingInformation: {
        fundingType: '',
        clientBankDetailId: '',
        customerNotes: '',
        coverLetter: {
          bankName: '',
          bankAddress: '',
          swift: '',
          pinCode: '',
          contactName: '',
          contactNumber: ''
        }
      },
      uploadedDocs: [],
      showCoverLetterSection: false,
      invoiceDetailsLoading: false,
      isIFProduct: false
    }
  },
  computed: {
    fields() {
      return {
        invoiceInformation: [
          {
            label: this.$i18n.t('invoices.add.form.seller'),
            value: this.invoiceInformation.seller
          },
          {
            label: this.$i18n.t('invoices.add.form.reference'),
            value: this.invoiceInformation.reference
          },
          {
            label: this.$i18n.t('invoices.add.form.invoiceDate'),
            value: this.invoiceInformation.invoiceDate
          },
          {
            label: this.$i18n.t('invoices.add.form.currency'),
            value: this.invoiceInformation.currencySymbol
          },
          {
            label: this.$i18n.t('invoices.add.form.grossValue'),
            value: this.invoiceInformation.grossValue
          },
          {
            label: this.$i18n.t('invoices.add.form.advanceReceived'),
            value: this.invoiceInformation.advanceReceived
          },
          {
            label: this.$i18n.t('invoices.add.form.value'),
            value: this.invoiceInformation.value
          },
          {
            label: this.$i18n.t('invoices.add.form.maxAmount'),
            value: this.invoiceInformation.maxAmountFormatted
          }
        ],
        shippingDetails: [
          ...(['es-MX', 'en-MX'].includes(this.$i18n.locale)
            ? []
            : [
                {
                  label: this.$i18n.t('invoices.add.form.shippingBillDate'),
                  value: this.shippingDetails.shippingBillDate
                },
                {
                  label: this.$i18n.t('invoices.add.form.shippingLine'),
                  value: this.shippingDetails.shippingLine
                },
                ...(!this.isIFProduct
                  ? [
                      {
                        label: this.$i18n.t('invoices.add.form.consignee'),
                        value: this.shippingDetails.consignee
                      }
                    ]
                  : []),
                {
                  label: this.$i18n.t('invoices.add.form.vesselName'),
                  value: this.shippingDetails.vesselName
                },
                {
                  label: this.$i18n.t('invoices.add.form.container'),
                  value: this.shippingDetails.containerNo
                },
                {
                  label: this.$i18n.t('invoices.add.form.blNo'),
                  value: this.shippingDetails.blNo
                },
                {
                  label: this.$i18n.t('invoices.add.form.blDate'),
                  value: this.shippingDetails.blDate
                }
              ]),
          {
            label: this.$i18n.t('invoices.add.form.userDueDate'),
            value: this.shippingDetails.userDueDate
          }
        ],
        fundingInformation: [
          {
            label: this.$i18n.t('invoices.add.form.fundingType'),
            value: this.fundingInformation.fundingType
          },
          {
            label: this.$i18n.t('invoices.add.form.clientBankDetailId'),
            value: this.fundingInformation.clientBankDetailId
          },
          {
            label: this.$i18n.t('invoices.add.form.customerNotes'),
            value: this.fundingInformation.customerNotes
          }
        ]
      }
    },
    disableSubmit() {
      return !this.hasPermission() || (this.isDone && !this.canEsign)
    },
    sections() {
      return [
        {
          title: this.$t('invoices.add.titles.invoiceInfo'),
          component: 'read-only-fields',
          data: this.fields.invoiceInformation,
          onEdit: () => {
            this.$mpTrack('Add Invoice - navigate back to basic-info')
            this.navigate('invoicesAdd', this.$route.params.id, 'basic-info')
          }
        },
        {
          title: this.$t('invoices.add.titles.shippingDetails'),
          component: 'read-only-fields',
          data: this.fields.shippingDetails,
          onEdit: () => {
            this.$mpTrack('Add Invoice - navigate back to shipping-details')
            this.navigate(
              'invoicesAdd',
              this.$route.params.id,
              'shipping-details'
            )
          }
        },
        {
          title: this.$t('invoices.add.titles.fundingInfo'),
          component: 'read-only-fields',
          data: this.fields.fundingInformation,
          onEdit: () => {
            this.$mpTrack('Add Invoice - navigate back to funding-details')
            this.navigate(
              'invoicesAdd',
              this.$route.params.id,
              'funding-details'
            )
          },
          stackUI: true,
          ...(this.showCoverLetterSection
            ? {
                subSectionLabel: this.$t('coverLetter.title'),
                subSectionData: [
                  {
                    label: this.$i18n.t('coverLetter.inputLabels.bankName'),
                    value: this.fundingInformation.coverLetter.bankName
                  },
                  {
                    label: this.$i18n.t('coverLetter.inputLabels.bankAddress'),
                    value: this.fundingInformation.coverLetter.bankAddress
                  },
                  {
                    label: this.$i18n.t('coverLetter.inputLabels.swift'),
                    value: this.fundingInformation.coverLetter.swift
                  },
                  {
                    label: this.$i18n.t('coverLetter.inputLabels.pinCode'),
                    value: this.fundingInformation.coverLetter.pinCode
                  },
                  {
                    label: this.$i18n.t('coverLetter.inputLabels.contactName'),
                    value: this.fundingInformation.coverLetter.contactName
                  },
                  {
                    label: this.$i18n.t(
                      'coverLetter.inputLabels.contactNumber'
                    ),
                    value: this.fundingInformation.coverLetter.contactNumber
                  }
                ]
              }
            : {})
        },
        {
          title: this.$t('invoices.add.titles.uploadDocs'),
          component: 'read-only-docs',
          data: this.uploadedDocs,
          onEdit: () => {
            this.$mpTrack('Add Invoice - navigate back to upload-docs')
            this.navigate('invoicesAdd', this.$route.params.id, 'upload-docs')
          }
        }
      ]
    },
    isDone() {
      return this.basicDocsUploaded
    },
    canEsign() {
      return this.exporter.parent_user_permissions['aadhar_esign_allowed?']
    },
    exporter() {
      return this.$store.getters.user.linked_users.find((exporter) => {
        return exporter.id === parseInt(this.$store.state.account.accountId)
      })
    },
    isIndia() {
      return this.$store.state.account.country === 'India'
    },
    isUSD() {
      return this.invoiceInformation.currency === 'usd'
    }
  },
  mounted() {
    this.$mpTrack(`Add Invoice - Review Step Loaded`)
    this.updateUserDripSwitch()
  },
  watch: {
    'exporterData.clients_locked': {
      handler() {
        this.loadInvoice()
      },
      deep: true
    }
  },
  methods: {
    navigateBack() {
      this.$mpTrack(`Add Invoice - navigate back to ${this.prevStep}`)
      this.navigate('invoicesAdd', this.$route.params.id, this.prevStep)
    },
    updateUserDripSwitch() {
      accountsService
        .getExporterAccountData(this.$store.state.account.accountId)
        .then((res) => {
          this.$store.dispatch(
            'setDripSwitchUserLevel',
            res.data.drip_switch_enabled
          )
          this.$store.dispatch('setTotalTransactionsConfig', {
            isInrTransactionCompleted:
              res.data.total_drip_switch_transactions > 0
          })
        })
        .catch()
    },
    loadInvoice() {
      this.invoiceDetailsLoading = true
      return invoiceService
        .fetchInvoicesById(this.$route.params.id, {
          include: ['form_data']
        })
        .then((res) => {
          this.isIFProduct = res.data['packing_list?']
          const data = res.data || {}
          const formData = data.form_data || {}

          this.importer = data.importer_user_id
          this.basicDocsUploaded = data['basic_documents_uploaded?']
          this.showCoverLetterSection =
            data['product_bank?'] && this.$i18n.locale.includes('IN')

          this.invoiceInformation.seller = data.exporter
          this.invoiceInformation.reference = data.reference
          this.invoiceInformation.invoiceDate = data.invoice_date
          this.invoiceInformation.currencySymbol = data.currsym
          this.invoiceInformation.currency = formData.currency
          this.invoiceInformation.grossValue = formData.gross_value
          this.invoiceInformation.advanceReceived = formData.advance_received
          this.invoiceInformation.value = data.value
          this.invoiceInformation.isDripSwitchEnabled = data.drip_switch_enabled

          this.shippingDetails.shippingBillDate = formData.shipping_bill_date
          this.shippingDetails.shippingLine = formData.shipping_line
          this.shippingDetails.consignee = formData.consignee
          this.shippingDetails.vesselName = formData.vessel_name
          this.shippingDetails.userDueDate = formData.user_due_date
          this.shippingDetails.containerNo = formData.container
          this.shippingDetails.blNo = formData.bl_no
          this.shippingDetails.blDate = formData.bl_date

          this.fundingInformation.fundingType =
            formData.customer_notes.startsWith('Invoices to be adjusted:')
              ? this.$t('invoices.add.form.invoiceReason.adjustment')
              : this.$t('invoices.add.form.invoiceReason.funding')
          this.fundingInformation.clientBankDetailId =
            data.settlement_customer_bank
          this.fundingInformation.customerNotes = formData.customer_notes
          if (this.showCoverLetterSection) {
            this.fundingInformation.coverLetter.bankName =
              formData.cover_letter_instruction.buyer_bank_name
            this.fundingInformation.coverLetter.bankAddress =
              formData.cover_letter_instruction.buyer_bank_address
            this.fundingInformation.coverLetter.swift =
              formData.cover_letter_instruction.swift_code
            this.fundingInformation.coverLetter.pinCode =
              formData.cover_letter_instruction.post_code
            this.fundingInformation.coverLetter.contactName =
              formData.cover_letter_instruction.contact_name
            this.fundingInformation.coverLetter.contactNumber =
              formData.cover_letter_instruction.contact_phone
          }

          this.uploadedDocs = data.documents_public

          this.getMaxFinanceableAmt()
          this.invoiceDetailsLoading = false
        })
        .catch((e) => {
          this.invoiceDetailsLoading = false
        })
    },
    getMaxFinanceableAmt() {
      if (
        !this.invoiceInformation.maxAmount &&
        this.exporterData &&
        this.exporterData.clients_locked.length
      ) {
        this.importer = this.getBuyerFromId(this.importer) || {}
        const calculatedValue = (() => {
          const grossValue = InputMasking.stripNaN(
            this.invoiceInformation.grossValue || 0
          )
          const advanceReceived = InputMasking.stripNaN(
            this.invoiceInformation.advanceReceived || 0
          )
          if (advanceReceived >= 0 && grossValue >= 0) {
            return InputMasking.convertToCurrency(grossValue - advanceReceived)
          }
        })()
        const calculatedVal = InputMasking.stripNaN(calculatedValue)
        const advanceRatePercentile =
          parseFloat(this.importer && this.importer.advance_rate) / 100
        this.invoiceInformation.maxAmount = this.importer
          ? (calculatedVal * advanceRatePercentile)
              .toFixed(2)
              .replace(/[.,]00$/, '')
          : 0
        this.invoiceInformation.maxAmountFormatted =
          InputMasking.convertToCurrency(this.invoiceInformation.maxAmount)
      }
    },
    hasPermission() {
      return this.$store.getters.hasInvoiceAddAndCreatePermission()
      /**
       * ?: below is the old code for access roles
        return (
          this.$store.getters.hasPermission('esign') ||
          this.$store.getters.hasPermission('edit')
        )
       **/
    },
    done(newInvoice) {
      this.$mpTrack('Add Invoice - Submit button clicked', {
        isNewInvoice: newInvoice,
        isBasicDocsUploaded: this.isDone,
        canEsign: this.canEsign
      })
      if (this.isDone) {
        this.showWarning()
        if (newInvoice) {
          return this.newInvoice()
        } else if (this.canEsign) {
          return this.showEsign()
        } else {
          return this.viewInvoice()
        }
      } else {
        if (newInvoice) {
          return this.newInvoice()
        } else {
          return this.viewInvoice()
        }
      }
    },
    showEsign() {
      return this.$router.push({
        name: 'invoicesEsign',
        query: {
          id: this.$route.params.id
        }
      })
    },
    viewInvoice() {
      return this.$router.push({
        name: 'invoicesSingle',
        params: {
          id: this.$route.params.id
        }
      })
    },
    newInvoice() {
      return this.$router.push({
        name: 'invoicesAdd'
      })
    },
    showWarning() {
      if (moment() > this.$store.state.constants.lateTime && !this.canEsign) {
        this.$notify({
          title: this.$t('invoices.warnings.late.title'),
          text: this.$t('invoices.warnings.late.text'),
          type: 'warn',
          duration: -1
        })
      }
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';

.review-step-container {
  font-family: $nunito;
  color: $dc-input-color;

  .heading {
    font-weight: 700;
  }

  .review-section {
    .header {
      .label {
        font-size: 1.625rem;
        font-weight: 700;

        &-sm {
          font-weight: 700;
          font-size: 1.5rem;
        }
      }
      .edit {
        cursor: pointer;
        font-size: 1.25rem;
        color: $primary;
        font-weight: 700;
      }
    }
  }
}
</style>
